import angular from 'angular';
import inventoryTemplate from './inventory.template.html';

/*******************************************************************************
 * Component: itemInventoryContent
 *
 * This is the component to be used to manage the inventory for a product
 * item in the AddChangeItemDialog. This will utilize the addChangeItemDialog's
 * component controller for shared data management.
 *******************************************************************************/
export const itemInventoryContentComponent = {
  require: { dialogCtrl: '^^addChangeItemDialogContent' },
  template: inventoryTemplate,
  controller: 'ItemInventoryController',
  transclude: {
    costAcctInfo: '?costAcctInfo',
    adjAcctInfo: '?adjAcctInfo'
  },
  controllerAs: '$invCtrl'
};

/*******************************************************************************
 * ItemInventoryController */
/**
 * Controller for the inventory tab of the add/change item dialog.
 *******************************************************************************/
/* @ngInject */ export function ItemInventoryController(
  $scope,
  AccountType,
  Alert,
  CommonUIGridTemplates,
  InventoryOptionsColumn,
  InventoryProduct,
  ItemAdminService,
  SERVER,
  Tools,
  uiGridConstants,
  $element,
  $transclude,
  CompilerService,
  $templateCache
) {
  /*****************************************************************************
   * Properties
   *****************************************************************************/
  /** Controller Instance. */ var mThis = this;
  /** Default Inventory Product Only. */ mThis.defaultOnly = true;
  /** Default Inventory Product. */ mThis.defaultProduct = null;
  /** Inventory Modified Flag. */ mThis.inventoryChanged = false;
  /** Inventory On Books. */ var mInventoryOnBooks =
    !!SERVER.productFeatures['INVENTORY-ONBOOKS'];
  /** Show Account Number Flag. */ mThis.showAccountNumber =
    SERVER.BooksWebConstants.showAcctNumber;
  /** Show Adjustment Account. */ mThis.showAdjustmentAccount =
    mInventoryOnBooks;
  /** Tracking inventory flag. */ var mTrackInventory = false;
  /** Unmatched InventoryProducts */ var mUnMatchedProducts = [];
  /** Validators object. */ mThis.validators = {};

  /** Show COGS Account for Inventory on books or ASB Works. */
  mThis.showCOGS = mInventoryOnBooks || SERVER.productID == 'ASB Works';

  /** List of ItemOptionMaster objects. */
  var mOptionMasterList = ItemAdminService.getOptions();

  /*-------------------------------*/
  /* Inventory Options Grid Config */
  /*-------------------------------*/
  var mInventoryColumn = new InventoryOptionsColumn();

  /*--------------------------------------*/
  /* Remove height set by angular-ui-grid */
  /* so the footer can be multiple rows.  */
  /*--------------------------------------*/
  var footerTemplate = $templateCache
    .get('ui-grid/ui-grid-footer')
    .replace(/^<div/, "<div style='height:initial'");

  /*------------------------------------------*/
  /* Grid to display item inventory options.  */
  /* This is used in the inventory tab.       */
  /*------------------------------------------*/
  mThis.dgInventory = {
    enableColumnMenus: false,
    enableHorizontalScrollbar: uiGridConstants.scrollbars.NEVER,
    enableVerticalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,
    enableSorting: false,
    enableRowHeaderSelection: false,
    multiSelect: false,
    showColumnFooter: true,
    minRowsToShow: 3,
    maxRowsToShow: 5,
    footerTemplate: footerTemplate,
    onRegisterApi: function (gridApi) {
      mThis.dgInventory.api = gridApi;

      /*------------------------------------------------------*/
      /* Update the input fields to the actual values on edit */
      /*------------------------------------------------------*/
      gridApi.edit.on.afterCellEdit(
        gridApi.grid.appScope,
        function (rowEntity, colDef) {
          if (colDef.name == 'Count') rowEntity.qtyInput = rowEntity.qty;

          if (colDef.name == 'cost') rowEntity.costInput = rowEntity.cost;

          if (colDef.name == 'Reserved')
            rowEntity.encumberanceInput = rowEntity.encumberance;
        }
      );
    },
    data: [],
    columnDefs: [
      {
        name: 'active?',
        /**
         * If the inventory is currently active AND has a quantity, it cannot be deactivated.
         * If the inventory is currently inactive, it should always be able to be set back to active.
         *
         * This situation where the inventory is inactive AND has a quantity would only occur
         * if the item was sold, the user marked the inventory inactive, and then the item was returned.
         * Otherwise, this situation can only happen due to data manipulation on the Dev side, and should
         * NOT be possible by the user.
         * We updated this logic in the habanero release due to a situation we ran into in this ticket:
         * https://graystep.atlassian.net/browse/GR-1470
         */
        cellTemplate:
          '<input type="checkbox" ng-model="row.entity.active" ng-disabled="!!row.entity.qty && row.entity.active">',
        type: 'boolean',
        enableSorting: false,
        cellEditableCondition: function (scope) {
          return !scope.row.entity.qty;
        },
        maxWidth: 80
      },
      Object.assign({}, mInventoryColumn, {
        footerCellTemplate: 'options-grid-name-footer-cell.html'
      }),
      {
        name: 'OnHand',
        field: 'qtyInput',
        enableCellEdit: true,
        enableCellEditOnFocus: true,
        editableCellTemplate: CommonUIGridTemplates.CELL_NUMBER_INPUT,
        precision: 0,
        allowNegative: false,
        enableSorting: false,
        cellFilter: 'number',
        cellEditableCondition: function (scope) {
          return !!scope.row.entity.active;
        },
        aggregationType: uiGridConstants.aggregationTypes.sum,
        footerCellTemplate: 'options-grid-qty-footer-cell.html',
        maxWidth: 120
      },
      {
        name: 'Reserved',
        field: 'encumberanceInput',
        enableCellEdit: true,
        enableCellEditOnFocus: true,
        editableCellTemplate: CommonUIGridTemplates.CELL_NUMBER_INPUT,
        precision: 0,
        allowNegative: false,
        enableSorting: false,
        cellFilter: 'number',
        aggregationType: uiGridConstants.aggregationTypes.sum,
        footerCellTemplate: 'options-grid-encumberance-footer-cell.html',
        maxWidth: 120
      },
      {
        name: 'cost',
        field: 'costInput',
        cellFilter: 'currency',
        enableCellEdit: true,
        enableCellEditOnFocus: true,
        editableCellTemplate: CommonUIGridTemplates.CELL_NUMBER_INPUT,
        precision: 2,
        allowNegative: false,
        enableSorting: false,
        cellEditableCondition: function (scope) {
          return !!scope.row.entity.active;
        },
        aggregationType: uiGridConstants.aggregationTypes.avg,
        footerCellFilter: 'currency',
        footerCellTemplate: 'options-grid-cost-footer-cell.html',
        maxWidth: 120
      }
    ]
  };

  /***************************************************************************
   * adjustmentRequired */
  /**
   * Returns an indication if an adjustment is required. This is indicated by any
   * inventory product having an adjustment needed.
   *
   * @return Boolean  True=adjustment is required.
   ***************************************************************************/
  mThis.adjustmentRequired = function () {
    return mThis.item.inventoryProducts.some(function (ip) {
      return ip.hasAdjustment;
    });
  };

  /***************************************************************************
   * COGSRequired */
  /**
   * Returns an indication that the COGS account is required.
   *
   * @return  Boolean True=COGS required.
   ***************************************************************************/
  mThis.COGSRequired = function () {
    return (
      mTrackInventory &&
      mThis.showCOGS &&
      mThis.item.type != SERVER.BooksDBConstants.IT_GROUP
    );
  };

  /***************************************************************************
   * distributeUnmatched */
  /**
   * Distribute the quantity of a combination across any new combinations that
   * contain the base combination. If there were no new options added, this
   * will not do anything. The distribution will be an even split.
   *
   * If the quantity cannot be divided evenly, this routine will go through
   * each product and add/subtract one until the total between all the new
   * combinations matches the original amount.
   *
   * @param  unMatchedProducts  List of unmatched combinations.
   * @param  combos             List of combinations to be displayed. The
   *                            elements within this list will be changed if
   *                            needed.
   ***************************************************************************/
  function distributeUnmatched(unMatchedProducts, combos) {
    /*-----------------------------------------------------------*/
    /* Spread the total quantity of the unmatched options across */
    /* any new combinations that contain the unmatched ones.     */
    /*-----------------------------------------------------------*/
    angular.forEach(unMatchedProducts, function (product) {
      var newCombinations = findCombinationsContaining(product.options, combos);
      var newComboLength = newCombinations ? newCombinations.length : 0;
      var splitQty = newComboLength > 0 ? product.qty / newComboLength : 0;
      var roundedQty = Math.round(Math.abs(splitQty)) * (splitQty < 0 ? -1 : 1);
      var a = [];
      var totalQty = 0;

      for (var i = 0; i < newComboLength; i++) {
        a.push(roundedQty);
        totalQty += roundedQty;
      }

      /*---------------------------------------------------*/
      /* If the rounded value doesn't match the total from */
      /* the product, we need to increase or decrease the  */
      /* individual values until the totals match.         */
      /*---------------------------------------------------*/
      for (i = 0; i < a.length; i++)
        if (totalQty != product.qty)
          if (product.qty > totalQty) {
            a[i]++;
            totalQty++;
          } else {
            a[i]--;
            totalQty--;
          }
        else break;

      /*---------------------------------------------*/
      /* Clear the quantity for the old combination. */
      /*---------------------------------------------*/
      product.newQty = 0;

      /*-----------------------------------------------------*/
      /* Set the distributed values on the new combinations. */
      /*-----------------------------------------------------*/
      for (i = 0; i < newComboLength; i++) newCombinations[i].qty += a[i];
    });
  }

  /***************************************************************************
   * findCombinationsContaining */
  /**
   * Finds all combinations that contain the list of <b>options</b>.
   *
   * @param  options  InventoryProductOptions to search for.
   * @param  products Inventory Products to search through.
   * @return          List of all combinations that contain the the option
   *                  combination provided.
   ***************************************************************************/
  function findCombinationsContaining(options, products) {
    if (options.length == 0) return null;

    /*--------------------------------------------------------------*/
    /* Go through each Inventory Product and compare each option to */
    /* find a match. The options passed in needs to be the outer    */
    /* loop, while the options from these products is the inner     */
    /* loop. This way we can match the options passed in, without   */
    /* needing to look through all the options of the product.      */
    /*--------------------------------------------------------------*/
    var matchedProducts = [];
    angular.forEach(products, function (product) {
      var foundAll = true;

      /*-------------------------------------------------------------*/
      /* Go through all the options passed in to compare the values. */
      /*-------------------------------------------------------------*/
      for (var o2 = 0; o2 < options.length; o2++) {
        var found = false;

        /*-------------------------------------------------*/
        /* If a match is found, set the <i>found</i> flag  */
        /* and stop iterating through this set of options. */
        /*-------------------------------------------------*/
        for (var i = 0; i < product.options.length; i++)
          if (product.options[i].itemOptionID == options[o2].itemOptionID) {
            found = true;
            break;
          }

        /*----------------------------------------------------------------------------*/
        /* If the option was found, continue looking through the options provided.    */
        /* If there was no match found, then this option doesn't exist in the         */
        /* product, and we can stop looking through the rest of the provided options. */
        /*----------------------------------------------------------------------------*/
        foundAll = foundAll && found;
        if (!foundAll) break;
      }

      /*----------------------------------------*/
      /* Add product if all options were found. */
      /*----------------------------------------*/
      if (foundAll) matchedProducts.push(product);
    });

    return matchedProducts;
  }

  /***************************************************************************
   * makeInventoryProducts */
  /**
   * Converts the item's Item Option objects into all possible combinations of
   * Inventory Product objects.
   *
   * @return Array of inventory products. The default product will be the first
   *         in the array.
   ***************************************************************************/
  function makeInventoryProducts() {
    /** Lookup object for ItemOptionMaster objects by Option ID. */
    var optionMasters = {};

    /*--------------------------------------------------------*/
    /* Inline function to return corresponding option master. */
    /*--------------------------------------------------------*/
    var getOption = function (id) {
      var prop = id.toString();

      /*-----------------------------------*/
      /* Return existing option for the id */
      /*-----------------------------------*/
      if (optionMasters.hasOwnProperty(prop)) return optionMasters[prop];
      /*----------------------------------------------------------*/
      /* Otherwise find corresponding ItemOptionMaster and update */
      /*----------------------------------------------------------*/ else
        return (optionMasters[prop] = mOptionMasterList
          .filter(function (mo) {
            return id == mo.id;
          })
          .shift());
    };

    /*-----------------------------------------------*/
    /* Group the ItemOptions by the ItemOption's     */
    /* optionID for active selection list types only */
    /*-----------------------------------------------*/
    var itemOptionsDict = {};

    (mThis.item.itemOptions || []).forEach(function (io) {
      var option = getOption(io.optionID);

      /*-----------------------------------------------*/
      /* Skip if no matching option master was found   */
      /*-----------------------------------------------*/
      if (!option) return;

      /*-----------------------------------------*/
      /* Restrict to active selection list types */
      /*-----------------------------------------*/
      if (
        option.type != SERVER.BooksDBConstants.MOT_SELECTION_LIST ||
        !io.active
      )
        return;

      /*-----------------------------------------------*/
      /* Create dictionary record for option if needed */
      /*-----------------------------------------------*/
      if (!itemOptionsDict.hasOwnProperty(option.id))
        itemOptionsDict[option.id] = [];

      /*-----------------------*/
      /* Update the dictionary */
      /*-----------------------*/
      itemOptionsDict[option.id].push({
        itemOptionID: io.id,
        itemOptionValue: io.value,
        masterOptionID: option.id,
        masterOptionName: option.name
      });
    });

    /*-------------------------------------------*/
    /* Create Inventory Products for each        */
    /* combination of Inventory Product Options. */
    /*-------------------------------------------*/
    var combos = [];
    angular.forEach(Object.keys(itemOptionsDict), function (key) {
      var newCombos = [];

      /*---------------------------*/
      /* Loop through each option. */
      /*---------------------------*/
      angular.forEach(itemOptionsDict[key], function (inventoryOption) {
        /*------------------------------------------*/
        /* First time through, create the Inventory */
        /* Prouduct and add this option to it.      */
        /*------------------------------------------*/
        if (combos.length == 0) {
          var inventoryProduct = new InventoryProduct();

          inventoryProduct.addOption(inventoryOption);

          /*------------------------------------------------------------*/
          /* Make sure to set the preset cost from the default product. */
          /*------------------------------------------------------------*/
          inventoryProduct.presetCost = mThis.item.defaultProduct
            ? mThis.item.defaultProduct.presetCost
            : 0;
          newCombos.push(inventoryProduct);
        } else
          /*---------------------------------------*/
          /* Otherwise, add this option to each    */
          /* of the previous sets of combinations. */
          /*---------------------------------------*/
          angular.forEach(combos.concat(), function (product) {
            product.addOption(angular.copy(inventoryOption));
            newCombos.push(product.clone());
          });
      });

      combos = newCombos;
    });

    /*---------------------------------------------------------*/
    /* For each Inventory Product in the combination list,     */
    /* match it to the Inventory Product from the Product Item */
    /* based on the options, and set the needed properties.    */
    /*---------------------------------------------------------*/
    for (var comboIndex = 0; comboIndex < combos.length; comboIndex++) {
      var combinationProduct = combos[comboIndex];

      for (
        var productIndex = 0;
        productIndex < mThis.item.inventoryProducts.length;
        productIndex++
      ) {
        var inventoryProduct = mThis.item.inventoryProducts[productIndex];

        if (inventoryProduct.equals(combinationProduct)) {
          angular.extend(combinationProduct, inventoryProduct, {
            options: combinationProduct.options
          });
          break;
        }
      }
    }

    /*----------------------------------------------------------*/
    /* Find all unmatched combinations. This will occur because */
    /* of new options being added, or options being removed.    */
    /*----------------------------------------------------------*/
    mUnMatchedProducts = [];
    angular.forEach(mThis.item.inventoryProducts, function (prod1) {
      /*---------------------------------------*/
      /* Skip the default product and products */
      /* that have already been saved.         */
      /*---------------------------------------*/
      if (prod1 == mThis.item.defaultProduct || Tools.isNaNZero(prod1.qty))
        return;

      var found = combos.some(function (prod2) {
        return prod1.equals(prod2);
      });

      if (!found) {
        prod1.active = isNaN(prod1.id);
        mUnMatchedProducts.push(prod1);
      }
    });

    /*----------------------------------*/
    /* Distribute any unmatched combos. */
    /*----------------------------------*/
    if (mUnMatchedProducts.length > 0)
      distributeUnmatched(mUnMatchedProducts, combos);

    /*------------------------------------------------*/
    /* Add the default product to the rest of the     */
    /* products. The default product should be first. */
    /*------------------------------------------------*/
    return [mThis.item.defaultProduct].concat(combos);
  }

  /*****************************************************************************
   * onInit */
  /**
   * Component initialization callback function. Set the validator object reference
   * on the tab object.
   *****************************************************************************/
  mThis.$onInit = function () {
    mThis.dialogCtrl.tabInventory.validators = mThis.validators;

    /*------------------------------------------------------------------*/
    /* Insert transcluded content and add reference to this controller. */
    /*------------------------------------------------------------------*/
    CompilerService.transcludeSlots($element, { $invCtrl: this }, $transclude);

    /*----------------------------------------------------------------*/
    /* Place a method on the parent dialog controller to check if the */
    /* provided optionID is currently used in the list of             */
    /* inventoryProducts and has a quantity > 0 and reserve > 0. This */
    /* method can be accessed from other tab content controllers.     */
    /*----------------------------------------------------------------*/
    mThis.dialogCtrl.checkOptionID = function (optionID) {
      return mThis.dgInventory.data.some(function (dgi) {
        return dgi.options.some(function (o) {
          return (
            o.itemOptionID == optionID &&
            (dgi.qtyInput > 0 || dgi.encumberanceInput > 0)
          );
        });
      });
    };

    /*-------------------------------------------------------------------*/
    /* Set up the onSelect tab handler to refresh the grid. A bug in   */
    /* ui-grid causes column headers to disappear when the viewport    */
    /* size changes or when a grid hidden in the DOM is shown.         */
    /*-------------------------------------------------------------------*/
    mThis.dialogCtrl.tabInventory.onSelect = function () {
      if (mThis.dgInventory.api) mThis.dgInventory.api.core.refresh();
    };

    /*------------------------------------------*/
    /* Set initialized to true so grid can load */
    /*------------------------------------------*/
    mThis.initialized = true;
  };

  /*****************************************************************************
   * optionsChanged */
  /**
   * Called by the watchers when options have been modified. Remakes the
   * inventory for inventory tracked product items.
   *****************************************************************************/
  function optionsChanged(newVal, oldVal) {
    /*--------------------------*/
    /* Do nothing on first run. */
    /*--------------------------*/
    if (newVal == oldVal) return;

    /*--------------------------------------*/
    /* Do nothing for non-products or items */
    /* that aren't tracking inventory.      */
    /*--------------------------------------*/
    if (!mThis.dialogCtrl.uiFlags.isProduct || !mTrackInventory) return;

    mInventoryColumn.setMasterOptions(
      Tools.mapUniqueObjects(mThis.item.itemOptions, 'optionID', [
        { optionID: 'id' },
        { optionName: 'name' }
      ])
    );
    updateInventory();
    mThis.inventoryChanged = true;
  }

  /*****************************************************************************
   * readyForCommit */
  /**
   * This is called as a custom validator method. This will always return null(valid)
   * but since this is called during validation, we can clean up the necessary
   * inventory data to ready the item for saving.
   *****************************************************************************/
  mThis.readyForCommit = function () {
    /*----------------------------------------------------------------*/
    /* Clear the default product's quantity when tracking by options. */
    /*----------------------------------------------------------------*/
    if (!mThis.defaultOnly) {
      mThis.item.defaultProduct.qtyInput = 0;
      mThis.item.defaultProduct.encumberanceInput = 0;
      mThis.item.defaultProduct.costInput = 0; //mThis.totalAverageCost();
    }

    /*----------------------*/
    /* Set the preset cost. */
    /*----------------------*/
    (mThis.item.inventoryProducts || []).forEach(function (ip) {
      if (!ip.costFromInventory && Tools.isNaNZero(ip.count))
        ip.presetCost = ip.cost;
    });

    /*-----------------------------------------------------------*/
    /* Add the unmatched options to the end of the inventory     */
    /* products so the changes to their quantities can be saved. */
    /*-----------------------------------------------------------*/
    Array.prototype.push.apply(
      mThis.item.inventoryProducts,
      mUnMatchedProducts
    );

    /*-----------------------------------------------------*/
    /* Adjustment required, add adjustment to the product. */
    /*-----------------------------------------------------*/
    if (mThis.adjustmentRequired())
      mThis.item.inventoryAdjustment = mThis.adjustment;
    else mThis.item.inventoryAdjustment = null;

    return null;
  };

  /***************************************************************************
   * totalAverageCost */
  /**
   * Returns the total average cost of all InventoryProduct objects in the
   * dgInventory grid. Used in the dgInventory footer template.
   *
   * @return  Integer   Total cost of all inventory products in the dgInventory
   *                    grid.
   ***************************************************************************/
  mThis.totalAverageCost = function () {
    var cost = 0;
    var count = 0;

    for (var i = 0; i < mThis.dgInventory.data.length; i++) {
      cost +=
        (mThis.dgInventory.data[i].cost || 0) *
        (mThis.dgInventory.data[i].qty || 0);
      count += mThis.dgInventory.data[i].qty || 0;
    }

    return count != 0 ? Tools.roundToPrecision(cost / count, 2) : 0;
  };

  /*****************************************************************************
   * trackInventory */
  /**
   * This is going to be a getter/setter used with `Track Inventory` checkbox
   * ngModel used to manage the tracking of inventory.
   *****************************************************************************/
  mThis.trackInventory = function (value) {
    /** Getter . */
    if (!arguments.length) return mTrackInventory;

    /** Setter. */
    if (!mThis.item || !mThis.dialogCtrl.uiFlags.isProduct) return;

    /*-------------------------------*/
    /* Attempting to remove tracking */
    /*-------------------------------*/
    if (!value) {
      /*-------------------------------------------------*/
      /* Prevent change while there are still quantities */
      /*-------------------------------------------------*/
      if (
        (mThis.item.inventoryProducts || []).some(function (ip) {
          return !Tools.isNaNZero(ip.qty);
        })
      ) {
        Alert.info(
          'This item still has available inventory and needs to be zeroed out before its tracking can be changed.'
        );
        return;
      }
    } else {
      /*-------------------------------*/
      /* Attempting to enable tracking */
      /*-------------------------------*/
      var hasSplitTrust =
        mThis.item.salesAcctList.length > 1 &&
        mThis.item.salesAcctList.some(function (isa) {
          return (
            mThis.dialogCtrl.COGSAccountList.filter(function (acct) {
              return (
                acct.accountID == isa.accountID &&
                acct.type == AccountType.TRUST
              );
            }).length > 0
          );
        });

      /*-------------------------------------------------------*/
      /* If the sales account is being split, and at least one */
      /* of them is a Trust, prevent inventory being tracked.  */
      /*-------------------------------------------------------*/
      if (hasSplitTrust) {
        Alert.info(
          'This item is split with at least one trust account, and cannot be tracked.'
        );
        return;
      }

      // use the qty to redistribute $scope.data.quantityOnHand.count;

      /*---------------------------------------------------------*/
      /* Ensure all inventory product permutations are available */
      /*---------------------------------------------------------*/
      updateInventory();
    }

    /*------------------------------------------------------------------*/
    /* Update the protected property and any defined inventory products */
    /*------------------------------------------------------------------*/
    mTrackInventory = value;
    angular.forEach(mThis.item.inventoryProducts, function (ip) {
      ip.active = mTrackInventory;
    });
  };

  /***************************************************************************
   * updateInventory */
  /**
   * Ensures the list of InventoryProducts is synced with the defined item options
   * on the item.
   ***************************************************************************/
  function updateInventory() {
    /*-------------------------------*/
    /* No item to manipulate, abort. */
    /*-------------------------------*/
    if (!mThis.item) return;

    /*-----------------------------------*/
    /* Build list of inventory products. */
    /*-----------------------------------*/
    mThis.item.inventoryProducts = makeInventoryProducts();
    mThis.defaultOnly = mThis.item.inventoryProducts.length == 1;

    /*---------------------------------*/
    /* Set the grid to the non-default */
    /* inventory products when used.   */
    /*---------------------------------*/
    if (!mThis.defaultOnly) {
      var product = mThis.item.defaultProduct;
      var options = mThis.item.inventoryProducts.filter(function (ip) {
        return ip != product;
      });
      mThis.dgInventory.data = options;

      /*-------------------------------------------------------------------*/
      /* If the quantity on hand of the default product is not zero, there */
      /* is a glitch in the data and an adjustment is required. Set the    */
      /* server totals to the total of the default produt and all options. */
      /*                                                                   */
      /* This is confusing because the Quantity Reserved (encumbered) is   */
      /* always negative.                                                  */
      /*-------------------------------------------------------------------*/
      mThis.inventoryChanged = product.count != 0 || product.encumbered != 0;
      var optionsAvailable = options.reduce(function (total, ip) {
        return (total += ip.count);
      }, 0);
      var optionsReserved = options.reduce(function (total, ip) {
        return (total += ip.encumbered);
      }, 0);

      mThis.serverQty = {
        onHand:
          product.count -
          product.encumbered +
          (optionsAvailable - optionsReserved),
        reserved: product.encumbered + optionsReserved
      };
    }

    /*-----------------------------*/
    /* Setup the adjustment object */
    /*-----------------------------*/
    mThis.adjustment = {
      type: SERVER.BooksDBConstants.LET_INVENTORY_ADJUST,
      entryDate: new Date()
    };
  }

  /*---------------------------------------------------*/
  /* Set up an accessor the item property from the     */
  /* dialogCtrl. Use an empty object for data binding. */
  /*---------------------------------------------------*/
  Object.defineProperty(mThis, 'item', {
    get: function () {
      return mThis.dialogCtrl ? mThis.dialogCtrl.item : {};
    }
  });

  /*----------------------------------------*/
  /* Update the default product and grid    */
  /* source when the item reference changes */
  /*----------------------------------------*/
  $scope.$watch(
    function () {
      return mThis.item;
    },
    function (item) {
      if (!item) return;

      mTrackInventory = item.defaultProduct && item.defaultProduct.active;

      /*---------------------------------------------------------*/
      /* Exit if item is not a product or not inventory tracked. */
      /*---------------------------------------------------------*/
      if (!mThis.dialogCtrl.uiFlags.isProduct || !mTrackInventory) return;

      /*---------------------------------*/
      /* Build list of InventoryProducts */
      /*---------------------------------*/
      updateInventory();
    }
  );

  /*-----------------------------------------------*/
  /* Watch for changes to the list of item options */
  /* and remake the list of inventory items.       */
  /*-----------------------------------------------*/
  $scope.$watchCollection(function () {
    return mThis.item.itemOptions;
  }, optionsChanged);

  /*------------------------------------------------------------*/
  /* Watch the active flags on all item options. To avoid an    */
  /* expensive deep watch on an array of objects, map each item */
  /* option's active property to a binary representation in     */
  /* string format. Remake the inventory products on a change.  */
  /*------------------------------------------------------------*/
  $scope.$watch(function () {
    return (mThis.item.itemOptions || [])
      .map(function (io) {
        return io.active ? '1' : '0';
      })
      .join('');
  }, optionsChanged);
}
